import { Fragment } from 'react';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import * as yup from 'yup';

import CTASpinnerButton from 'components/buttons/button-spinner';
import InputField from 'components/forms/fields/input';
import FormLabel from 'components/forms/fields/label';
import SelectField from 'components/forms/fields/select';
import FormGroup from 'components/forms/form-group';

import FieldErrorPopup from './field-error-popup';

export type WebinarFormValues = {
  companyName: string,
  companySize?: string | undefined | null,
  emailAddress: string,
  fullName: string
  hasOccurrences: boolean
  occurrence?: string | null
}

export type WebinarFormOccurrence = {
  date: string
  key: string
}

export enum WebinarFormCompanySize {
  SMALL = '1-50',
  MID = '50-500',
  LARGE = '500+'
}

type InitialValuesProps = {
  hasOccurrences: boolean
  prefilledValues?: Partial<WebinarFormValues>
}
const initialValues = (props: InitialValuesProps): WebinarFormValues => {

  const emptyValues = {
    emailAddress: '',
    companyName: '',
    fullName: '',
    hasOccurrences: props.hasOccurrences,
    occurrence: '',
  };

  if (!props.prefilledValues) {
    return emptyValues;
  }

  return {
    ...emptyValues,
    ...props.prefilledValues,
  };
};

const schema: yup.Schema<Omit<WebinarFormValues, 'companySize'>> = yup.object({
  hasOccurrences: yup.boolean().required(),
  companyName: yup.string().required('Company name required'),
  emailAddress: yup.string().email('Invalid email').required('Email required'),
  fullName: yup.string().matches(/^\w+\s\w+/, 'Full name required').required('Full name required'),
  occurrence: yup.string().when(['hasOccurrences'], {
    is: true,
    then: schema => schema.required('Select a date'),
    otherwise: schema => schema.notRequired(),
  }),
});

type Props = {
  hideInputLabels?: true
  occurrences?: WebinarFormOccurrence[]
  onSubmit: (values: WebinarFormValues) => void | Promise<void>
  prefilledValues?: Partial<WebinarFormValues>
  submitButtonClassName?: string
  submitText?: string
}

const WebinarForm  = (props:Props) => {

  const hasOccurrences = () => !!props.occurrences && props.occurrences.length > 0;
  
  const handleSubmit = async (values: WebinarFormValues) => {
    await props.onSubmit(values);
  };

  return (
    <Formik
      initialValues={ initialValues({ hasOccurrences: hasOccurrences(), prefilledValues: props.prefilledValues }) }
      onSubmit={ handleSubmit }
      validationSchema={ schema }
    >
      {({
        errors,
        isSubmitting,
        handleChange,
        touched,
        values,
      }) => {

        const showOccurrenceError = hasOccurrences() && !!errors.occurrence && !!touched.occurrence;
        const showFirstNameError = !showOccurrenceError && !!errors.fullName && !!touched.fullName;
        const showEmailError = !showOccurrenceError && !showFirstNameError && !!errors.emailAddress && !!touched.emailAddress;
        const showCompanyNameError = !showOccurrenceError && !showFirstNameError && !showEmailError && !!errors.companyName && !!touched.companyName;

        return (
          <Fragment>
            <Form>
              
              <input
                type="checkbox"
                checked={ values.hasOccurrences }
                readOnly
                className="is-sr-only"
                tabIndex={ -1 }
              />
              {props.occurrences && (
                <FormGroup>
                  <FormLabel hidden htmlFor="occurrence">Webinar date</FormLabel>
                  <SelectField
                    id="occurrence"
                    hasError={ showOccurrenceError }
                    name="occurrence"
                    noBorder={ props.hideInputLabels }
                    onChange={ handleChange }
                    placeholder="Choose an Upcoming Session"
                    value={ values.occurrence ?? '' }
                  >
                    <option value="">Select...</option>
                    {props.occurrences.map(occurrence => (
                      <option key={ occurrence.key } value={ occurrence.key }>
                        {format(new Date(occurrence.date), 'MMMM d \'-\' h:mmaaa')}
                      </option>
                    ))}
                  </SelectField>
                  {showOccurrenceError && (
                    <FieldErrorPopup>{errors.occurrence}</FieldErrorPopup>
                  )}
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel hidden htmlFor="fullName">Full name</FormLabel>
                <InputField
                  id="fullName"
                  hasError={ showFirstNameError }
                  name="fullName"
                  onChange={ handleChange }
                  placeholder="Full Name"
                  value={ values.fullName }
                />
                {showFirstNameError && (
                  <FieldErrorPopup>{errors.fullName}</FieldErrorPopup>
                )}
              </FormGroup>
              <FormGroup>
                <FormLabel hidden htmlFor="emailAddress">Email address</FormLabel>
                <InputField
                  id="emailAddress"
                  hasError={ showEmailError }
                  name="emailAddress"
                  onChange={ handleChange }
                  placeholder="Email Address"
                  type="email"
                  value={ values.emailAddress }
                />
                {showEmailError && (
                  <FieldErrorPopup>{errors.emailAddress}</FieldErrorPopup>
                )}
              </FormGroup>
              <FormGroup>
                <FormLabel hidden htmlFor="companyName">Company Name</FormLabel>
                <InputField
                  id="companyName"
                  hasError={ showCompanyNameError }
                  name="companyName"
                  onChange={ handleChange }
                  placeholder="Company Name"
                  value={ values.companyName }
                />
                {showCompanyNameError && (
                  <FieldErrorPopup>{errors.companyName}</FieldErrorPopup>
                )}
              </FormGroup>

              <FormGroup>
                <CTASpinnerButton
                  className={ props.submitButtonClassName }
                  showSpinner={ isSubmitting }
                  type="submit"
                >
                  {props.submitText ?? 'Register For Webinar'}
                </CTASpinnerButton>
              </FormGroup>
            </Form>
          </Fragment>
        );
      }}
    </Formik>
  );
};

export default WebinarForm;
